import { type ReactNode, useId } from 'react';
import { ErrorBoundary } from '~/atoms';
import { ActionsRow, Alert, FileInput, Spinner } from '~/molecules';

import {
  type FormStateHandler,
  type ResolvedDataImportConfig,
  type ResolvedDataImportFile,
} from '../../dataImport.config';
import { type DataImportCopy } from '../../dataImport.copy';
import { type ParsedImportFile } from '../../dataImport.parsing';
import { type DataImportPreviewModel } from '../../dataImport.preview';
import { type DataImportWizardStepId } from '../../dataImportWizard.steps';
import { ColumnMappingForm } from '../ColumnMappingForm';
import { FormStateWrapper } from '../FormStateWrapper';
import { Instructions } from '../Instructions';
import { SubmitErrorRenderer } from '../SubmitErrorRenderer';

import { DataImportPreviewTable } from './DataImportPreviewTable';

/**
 * Only the slices this step renders, so a resolved config specialized on a
 * schema and a preview type stays assignable here.
 */
type FileStepConfig = Pick<
  ResolvedDataImportConfig,
  'instructions' | 'renderAdditionalForm' | 'templates'
> & {
  readonly file: Pick<ResolvedDataImportFile, 'acceptedFileTypes'>;
};

type FileStepProps = {
  readonly config: FileStepConfig;
  readonly copy: DataImportCopy;
  readonly file?: File;
  readonly formData: Record<string, unknown>;
  readonly isParsing: boolean;
  readonly onFormChange: FormStateHandler;
  readonly onRemoveFile: () => void;
  readonly onSelectFile: (file: File) => void;
  readonly parseError?: Error;
};

export function DataImportWizardFileStep({
  config,
  copy,
  file,
  formData,
  isParsing,
  onFormChange,
  onRemoveFile,
  onSelectFile,
  parseError,
}: FileStepProps) {
  const {
    file: fileConfig,
    instructions,
    renderAdditionalForm,
    templates,
  } = config;

  return (
    <div className="flex min-h-0 flex-1 flex-col gap-4 overflow-y-auto">
      <Instructions
        additionalInformationLink={instructions.additionalInformationLink}
        copy={copy}
        templates={templates}
        text={instructions.text}
      />
      {parseError ? <Alert severity="error">{parseError.message}</Alert> : null}
      <div className="flex flex-1 items-center justify-center">
        <FileInput
          accept={fileConfig.acceptedFileTypes}
          acceptedFileTypesLabel={copy.acceptedFileTypes(
            fileConfig.acceptedFileTypes,
          )}
          buttonText={file ? copy.selectAnotherFile : copy.selectFile}
          disabled={isParsing}
          getRemoveFileLabel={copy.removeFile}
          onChange={(selectedFiles) => {
            const selectedFile = Array.isArray(selectedFiles)
              ? selectedFiles[0]
              : selectedFiles;

            if (selectedFile) {
              onSelectFile(selectedFile);
            } else {
              onRemoveFile();
            }
          }}
        />
      </div>
      {isParsing ? <Spinner title={copy.parsingFile} /> : null}
      {renderAdditionalForm ? (
        <ErrorBoundary fallbackMessage={copy.additionalFormFallback}>
          <FormStateWrapper
            context={{ formData }}
            renderForm={renderAdditionalForm}
            onFormChange={onFormChange}
          />
        </ErrorBoundary>
      ) : null}
    </div>
  );
}

type MappingStepProps = {
  readonly columnMapping?: Record<string, string>;
  readonly copy: DataImportCopy;
  readonly fileConfig: ResolvedDataImportFile;
  readonly onColumnMappingChange: ({
    columnMapping,
    isValid,
  }: {
    columnMapping: Record<string, string>;
    isValid: boolean;
  }) => void;
  readonly parsedFile: ParsedImportFile;
};

export function DataImportWizardMappingStep({
  columnMapping,
  copy,
  fileConfig,
  onColumnMappingChange,
  parsedFile,
}: MappingStepProps) {
  return (
    <div className="min-h-0 flex-1 overflow-y-auto">
      <ColumnMappingForm
        copy={copy}
        fileConfig={fileConfig}
        headerFields={parsedFile.headerFields}
        initialColumnMapping={columnMapping}
        onColumnMappingChange={onColumnMappingChange}
      />
    </div>
  );
}

type RemotePreviewState<TPreview> =
  | { status: 'idle' }
  | { status: 'ready'; value: TPreview };

export type DataImportWizardPreviewRenderContext<TPreview> = {
  /**
   * The wizard's resolved copy, so a custom preview that builds its own
   * `DataImportPreviewTable` renders the same strings as `defaultPreview`.
   */
  readonly copy: DataImportCopy;
  readonly defaultPreview: ReactNode;
  readonly model: DataImportPreviewModel;
  readonly remotePreview: RemotePreviewState<TPreview>;
};

type PreviewStepProps<TPreview> = {
  readonly copy: DataImportCopy;
  readonly importError?: Error;
  readonly isImporting: boolean;
  readonly isValidating: boolean;
  readonly preview: RemotePreviewState<TPreview>;
  readonly previewError?: Error;
  readonly previewModel:
    | { status: 'idle' }
    | { status: 'ready'; value: DataImportPreviewModel };
  readonly renderPreview?: (
    context: DataImportWizardPreviewRenderContext<TPreview>,
  ) => ReactNode;
  readonly renderSubmitError?: (error: Error) => ReactNode;
};

/**
 * Invokes `renderPreview` from its own render, so a surrounding
 * `ErrorBoundary` catches a custom renderer that throws.
 */
function CustomPreviewRenderer<TPreview>({
  context,
  renderPreview,
}: {
  readonly context: DataImportWizardPreviewRenderContext<TPreview>;
  readonly renderPreview: (
    context: DataImportWizardPreviewRenderContext<TPreview>,
  ) => ReactNode;
}) {
  return <>{renderPreview(context)}</>;
}

export function DataImportWizardPreviewStep<TPreview>({
  copy,
  importError,
  isImporting,
  isValidating,
  preview,
  previewError,
  previewModel,
  renderPreview,
  renderSubmitError,
}: PreviewStepProps<TPreview>) {
  const previewContent =
    !isValidating && previewModel.status === 'ready' ? (
      renderPreview ? (
        <ErrorBoundary fallbackMessage={copy.previewFallback}>
          <CustomPreviewRenderer
            context={{
              copy,
              defaultPreview: (
                <DataImportPreviewTable
                  copy={copy}
                  model={previewModel.value}
                />
              ),
              model: previewModel.value,
              remotePreview: preview,
            }}
            renderPreview={renderPreview}
          />
        </ErrorBoundary>
      ) : (
        <DataImportPreviewTable copy={copy} model={previewModel.value} />
      )
    ) : null;

  return (
    <div className="flex min-h-0 flex-1 flex-col gap-4 overflow-hidden">
      {isValidating ? <Spinner title={copy.validatingPreview} /> : null}

      {previewError ? (
        <ErrorBoundary fallbackMessage={copy.submitErrorFallback}>
          <SubmitErrorRenderer
            error={previewError}
            renderSubmitError={renderSubmitError}
          />
        </ErrorBoundary>
      ) : null}

      {importError ? (
        <ErrorBoundary fallbackMessage={copy.submitErrorFallback}>
          <SubmitErrorRenderer
            error={importError}
            renderSubmitError={renderSubmitError}
          />
        </ErrorBoundary>
      ) : null}

      {previewContent}

      {isImporting ? <Spinner title={copy.importing} /> : null}
    </div>
  );
}

type ActionsProps = {
  readonly activeStep: DataImportWizardStepId | 'result';
  readonly canContinueFromFile: boolean;
  /** Why confirming is blocked, when the import's confirm policy says so. */
  readonly confirmBlockedReason?: string;
  readonly copy: DataImportCopy;
  /** Whether the resolved flow includes the mapping step; when it does not, the file step's primary action runs preview validation directly. */
  readonly hasMappingStep: boolean;
  readonly isColumnMappingValid: boolean;
  readonly isImporting: boolean;
  readonly isValidating: boolean;
  readonly onBack: () => void;
  readonly onCancel: () => void;
  readonly onConfirm: () => void;
  readonly onNextFromFile: () => void;
  readonly onNextFromMapping: () => void;
  readonly previewReady: boolean;
};

const actionRowClassName =
  'sticky bottom-0 z-10 mt-auto flex-row-reverse bg-white p-0';

function getPrimaryAction({
  activeStep,
  canContinueFromFile,
  confirmBlockedReasonId,
  copy,
  hasMappingStep,
  isColumnMappingValid,
  isConfirmBlockedReasonVisible,
  isImporting,
  isValidating,
  onConfirm,
  onNextFromFile,
  onNextFromMapping,
  previewReady,
}: {
  activeStep: DataImportWizardStepId;
  canContinueFromFile: boolean;
  confirmBlockedReasonId: string;
  copy: DataImportCopy;
  hasMappingStep: boolean;
  isColumnMappingValid: boolean;
  isConfirmBlockedReasonVisible: boolean;
  isImporting: boolean;
  isValidating: boolean;
  onConfirm: () => void;
  onNextFromFile: () => void;
  onNextFromMapping: () => void;
  previewReady: boolean;
}) {
  switch (activeStep) {
    case 'file':
      // Without a mapping step, this action runs the same preview validation
      // the mapping step's action would, so a fixed-format import can go
      // straight from a parsed file to its preview.
      return {
        disabled: !canContinueFromFile,
        onClick: onNextFromFile,
        text: hasMappingStep ? copy.mapColumnsAction : copy.validateAction,
      };
    case 'mapping':
      return {
        disabled: !isColumnMappingValid,
        onClick: onNextFromMapping,
        text: copy.validateAction,
      };
    case 'preview':
      return {
        'aria-describedby': isConfirmBlockedReasonVisible
          ? confirmBlockedReasonId
          : undefined,
        disabled: !previewReady || isValidating || isImporting,
        onClick: onConfirm,
        text: copy.confirmAction,
      };
  }
}

export function DataImportWizardActions({
  activeStep,
  canContinueFromFile,
  confirmBlockedReason,
  copy,
  hasMappingStep,
  isColumnMappingValid,
  isImporting,
  isValidating,
  onBack,
  onCancel,
  onConfirm,
  onNextFromFile,
  onNextFromMapping,
  previewReady,
}: ActionsProps) {
  const confirmBlockedReasonId = useId();

  if (activeStep === 'result') {
    return (
      <ActionsRow
        className={actionRowClassName}
        primaryAction={{
          text: copy.close,
          onClick: onCancel,
        }}
      />
    );
  }

  const isConfirmBlockedReasonVisible =
    activeStep === 'preview' && Boolean(confirmBlockedReason);
  const primaryAction = getPrimaryAction({
    activeStep,
    canContinueFromFile,
    confirmBlockedReasonId,
    copy,
    hasMappingStep,
    isColumnMappingValid,
    isConfirmBlockedReasonVisible,
    isImporting,
    isValidating,
    onConfirm,
    onNextFromFile,
    onNextFromMapping,
    previewReady,
  });
  const isFirstStep = activeStep === 'file';

  return (
    <ActionsRow
      className={actionRowClassName}
      primaryAction={primaryAction}
      secondaryAction={
        isFirstStep
          ? { text: copy.cancel }
          : {
              disabled: isValidating || isImporting,
              text: copy.back,
            }
      }
      onCancel={isFirstStep ? onCancel : onBack}
    >
      {isConfirmBlockedReasonVisible ? (
        <p
          className="min-w-0 text-sm wrap-break-word text-tiga-text-lighter"
          id={confirmBlockedReasonId}
        >
          {confirmBlockedReason}
        </p>
      ) : null}
    </ActionsRow>
  );
}
